import { ColumnsType } from 'antd/lib/table';
import { useEffect, useMemo, useState } from 'react';
import { ColumnState } from './ColumnMenuItem';
import { ColumnsDropdown } from './ColumnsDropdown';
import { selectHidden } from './columnSettingsUtils';
import { useColumnOptions } from './useColumnOptions';

const LOCAL_COLUMN_SETTINGS_PREFIX = 'bfp.table-columns.';

function getStorageKey(table: string) {
  return `${LOCAL_COLUMN_SETTINGS_PREFIX}${table}`;
}

function readHiddenColumns(key: string): string | undefined {
  try {
    return window.localStorage.getItem(key) ?? undefined;
  } catch (error) {
    console.error('Failed to read table column settings from localStorage.', {
      key,
      error,
    });
    return undefined;
  }
}

function writeHiddenColumns(key: string, hiddenColumns: string) {
  try {
    window.localStorage.setItem(key, hiddenColumns);
  } catch (error) {
    console.error('Failed to save table column settings to localStorage.', {
      key,
      error,
    });
  }
}

export function useLocalColumnsSelect<T>(
  table: string,
  columns?: ColumnsType<T>,
  defaultHiddenColumnKeys?: string[],
) {
  const storageKey = getStorageKey(table);
  const [hiddenColumns, setHiddenColumns] = useState<string | undefined>(() =>
    readHiddenColumns(storageKey),
  );

  useEffect(() => {
    setHiddenColumns(readHiddenColumns(storageKey));
  }, [storageKey]);

  const settings = useMemo(() => ({ hiddenColumns }), [hiddenColumns]);
  const { columnOptions, visibleColumns } = useColumnOptions(
    columns,
    settings,
    defaultHiddenColumnKeys,
  );

  const handleColumnsChanged = (nextColumns: ColumnState[]) => {
    const value = selectHidden(nextColumns);
    setHiddenColumns(value);
    writeHiddenColumns(storageKey, value);
  };

  return {
    columnsSelect: (
      <ColumnsDropdown value={columnOptions} onChange={handleColumnsChanged} />
    ),
    isLoading: false,
    visibleColumns,
  };
}
